import { Autocomplete, Box, Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLButton } from "OLButton";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useMember } from "api/members";
import { useReward } from "api/rewards";
import { useWallets } from "api/wallets";
import { DateTimeInput } from "components/FormControls/DateTimeInput";
import { OLCheckbox } from "components/FormControls/OLCheckbox";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { ReactComponent as ChevronDownIcon } from "components/Icons/chevron-down.svg";
import { OLModal } from "components/Tooltips/OLModal";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { isUndefined } from "lodash";
import { useState } from "react";
import { ListResponse } from "types/listing";
import { BackendMemberModel } from "types/members";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    formContainer: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(10),
    },
    SelectInput: {
      maxWidth: "100%",
      width: 406,
    },
    modalButton: {
      marginTop: theme.spacing(4),
    },
    checkbox: {
      color: theme.gray500,
      margin: 0,
      "& span": { padding: "0 9px 0 0" },
    },
    error: {
      color: theme.red500,
      display: "block",
      fontSize: 14,
      "& + &": { marginTop: 0 },
    },
    inputInfo: {
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "20px",
      color: theme.gray500,
      marginTop: theme.spacing(6),
    },
    listItem: {
      display: "flex",
      flexDirection: "column",
      alignContent: "left",
      padding: theme.spacing(0, 0, 4, 5),
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "#F5F5F5",
      },
    },
    listItemProp: { margin: theme.spacing(0), wordBreak: "break-all" },
  }),
);

export const MemberListItem = ({
  member,
  props,
}: {
  member?: BackendMemberModel;
  props: React.HTMLAttributes<HTMLLIElement> & {
    "data-hj-suppress"?: boolean;
    "data-option-index"?: number;
    "data-test-id"?: string;
  };
}) => {
  const classes = useStyles();
  const { message } = useMessage();

  return (
    <li
      {...props}
      className={classes.listItem}
      data-test-id={
        props?.["data-test-id"] && !isUndefined(props?.["data-option-index"])
          ? `${props?.["data-test-id"]}-${props?.["data-option-index"] + 1}`
          : undefined
      }
    >
      {member?.email && <p className={classes.listItemProp}>{member?.email}</p>}
      {member?.loyaltyCardNumber && (
        <p className={classes.listItemProp}>
          {`${message({
            id: "loyaltyCardNumber",
          })}: ${member?.loyaltyCardNumber}`}
        </p>
      )}
      {member?.phone && (
        <p className={classes.listItemProp}>
          {`${message({ id: "phoneNumber" })}:
    ${member?.phone}`}
        </p>
      )}
      {member?.customerId && (
        <p className={classes.listItemProp}>
          {`${message({ id: "customerId" })}:
    ${member?.customerId}`}
        </p>
      )}
    </li>
  );
};

export type ClaimRewardData = {
  id: string;
  type: string;
  dateValid?: string;
};

const ClaimRewardModal = ({
  closeModal,
  open,
  reward,
}: {
  closeModal: () => void;
  open: boolean;
  reward: ClaimRewardData;
}) => {
  const classes = useStyles();
  const { claimReward } = useReward();
  const { message } = useMessage();
  const { notify } = useNotifications();

  const [walletTypeSearch, setWalletTypeSearch] = useState("");

  const { useInfiniteWalletsList } = useWallets();

  const {
    data: infiniteWalletsList,
    size: walletsSize,
    setSize: setWalletsSize,
    isValidating,
  } = useInfiniteWalletsList({
    filters: walletTypeSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: walletTypeSearch,
          },
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ]
      : [
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ],
    order: "asc",
    sortKey: "name",
  });

  const { infiniteData: walletsList, anyElementsToFetch: anyWalletsToFetch } =
    useManageInfiniteScroll(infiniteWalletsList || []);

  const { useMembersList } = useMember();
  const [searchedMemberValue, setSearchedMemberValue] = useState("");
  const { data: searchedMembers } = useMembersList({
    filters: searchedMemberValue
      ? [
          {
            condition: "",
            query: "identifier",
            value: searchedMemberValue,
          },
        ]
      : [],
  }) as {
    data: ListResponse;
  };

  const getMemberLabel = (option: BackendMemberModel) => {
    if (option.email) {
      return option.email;
    } else if (option.loyaltyCardNumber) {
      return `${message({ id: "loyaltyCardNumber" })}: ${
        option.loyaltyCardNumber
      }`;
    } else if (option.phone) {
      return `${message({ id: "phoneNumber" })}: ${option.phone}`;
    } else if (option.customerId) {
      return `${message({ id: "customerId" })}: ${option.customerId}`;
    } else {
      return "";
    }
  };

  return (
    <OLModal
      allowOverflow
      header={message({ id: "claimReward" })}
      onClose={closeModal}
      open={open}
    >
      <Formik
        initialValues={{
          couponValue: 0,
          customerId: "",
          rewardWalletCode: null,
          quantity: 0,
          units: 0,
          withoutPoints: false,
          specificExpiringDate: !!reward.dateValid,
          dateValid: reward.dateValid,
        }}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          setSubmitting(true);
          setStatus({});
          const properties = {
            ...values,
            ...(reward.type !== "dynamic_coupon" && { couponValue: undefined }),
            ...(reward.type !== "conversion_coupon" && { units: undefined }),
            ...(reward.type === "conversion_coupon" && {
              quantity: undefined,
              withoutPoints: undefined,
            }),
            specificExpiringDate: undefined,
          };
          try {
            await claimReward(reward.id, properties);
            notify(message({ id: "rewardSuccesfullyPurchased" }));
            closeModal();
          } catch (exception) {
            setSubmitting(false);
            notify(getErrorMessage(exception));
            const errors = backendErrors(getBackendErrors(exception));
            setStatus(errors);
            focusError();
          }
        }}
      >
        {({ handleSubmit, isSubmitting, setFieldValue, status, values }) => {
          return (
            <Form className={classes.formContainer} onSubmit={handleSubmit}>
              {status?.[""] && (
                <span className={classes.error}>{status[""]}</span>
              )}
              <Autocomplete
                className={classes.SelectInput}
                renderOption={(props, member: BackendMemberModel) => (
                  <MemberListItem
                    props={{ ...props, "data-hj-suppress": true }}
                    member={member}
                  />
                )}
                popupIcon={<ChevronDownIcon />}
                filterOptions={(options: BackendMemberModel[]) => options}
                getOptionLabel={(option: BackendMemberModel) =>
                  getMemberLabel(option)
                }
                onBlur={(event) => {
                  event && setSearchedMemberValue("");
                }}
                onChange={(_, newInputValue) => {
                  if (newInputValue)
                    setFieldValue("customerId", newInputValue.customerId);
                }}
                onInputChange={(e) =>
                  setSearchedMemberValue(
                    (e.target as HTMLInputElement)["value"],
                  )
                }
                options={searchedMembers ? searchedMembers.items : []}
                renderInput={(params) => (
                  <TextInput
                    {...params}
                    className={classes.SelectInput}
                    InputLabelProps={{ shrink: true }}
                    label={
                      <RequiredFieldLabel
                        text={message({ id: "selectMember" })}
                      />
                    }
                    name="customerId"
                    placeholder={message({ id: "selectMemberInfo" })}
                    data-test-id="claim-reward-member-select"
                  />
                )}
              />

              {reward.type === "dynamic_coupon" && (
                <TextInput
                  className={classes.SelectInput}
                  label={message({ id: "couponValue" })}
                  name="couponValue"
                  type="number"
                />
              )}
              {reward.type !== "conversion_coupon" ? (
                <TextInput
                  className={classes.SelectInput}
                  label={message({ id: "quantity" })}
                  name="quantity"
                  type="number"
                  data-test-id="quantity"
                />
              ) : (
                <TextInput
                  className={classes.SelectInput}
                  label={message({ id: "unitsToConvert" })}
                  name="units"
                  type="number"
                  data-test-id="units-to-convert"
                />
              )}
              {reward.type !== "material" && (
                <>
                  <OLCheckbox
                    className={classes.checkbox}
                    label={message({ id: "overrideTheExpirationDate" })}
                    name="specificExpiringDate"
                  />
                  {values.specificExpiringDate && (
                    <DateTimeInput
                      className={classes.SelectInput}
                      format="yyyy-MM-dd HH:mmXXX"
                      label={
                        <RequiredFieldLabel
                          text={message({ id: "couponExpirationDate" })}
                        />
                      }
                      name="dateValid"
                    />
                  )}
                </>
              )}
              <Box>
                <SelectInput
                  className={classes.SelectInput}
                  displaySearch
                  items={walletsList.map((wallet) => ({
                    label: wallet.name,
                    id: wallet.code,
                  }))}
                  asyncSearch={(searchedQuery) =>
                    setWalletTypeSearch(searchedQuery)
                  }
                  label={message({
                    id: "walletTypeAllowedForRedemption",
                  })}
                  name="rewardWalletCode"
                  placeholder={message({
                    id: "walletTypeAllowedForRedemption",
                  })}
                  loadMoreElements={() =>
                    !isValidating && setWalletsSize(walletsSize + 1)
                  }
                  anyElementsToFetch={anyWalletsToFetch}
                />

                {!values.rewardWalletCode && (
                  <Typography className={classes.inputInfo}>
                    {message({ id: "rewardWalletTypeClaimInfo" })}
                  </Typography>
                )}
              </Box>
              {reward.type !== "conversion_coupon" && (
                <OLCheckbox
                  className={classes.checkbox}
                  label={message({ id: "withoutPoints" })}
                  name="withoutPoints"
                  data-test-id="without-points"
                />
              )}
              <OLButton
                className={classes.modalButton}
                color="primary"
                disabled={isSubmitting}
                fullWidth
                type="submit"
                variant="contained"
                data-test-id="claim-reward"
              >
                {message({ id: "claimReward" })}
              </OLButton>
            </Form>
          );
        }}
      </Formik>
    </OLModal>
  );
};

export default ClaimRewardModal;
